import { useState } from "react";
import { doc, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../firebase";

export default function PackageStatusModal({
  patient,
  activePackage,
  totalDueAmount,
  visits,
  onClose,
  onSuccess,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmForceClose, setConfirmForceClose] = useState(false);

  if (!patient) return null;

  const handleClosePackage = async () => {
    setError("");
    // 🔴 SAFETY CHECK 3: Unpaid consultations
    const unpaidConsultations = visits?.filter(
      (v) => v.consultation_payment_status === "UNPAID" && !v.used_from_package, // package sessions exclude
    );

    if (unpaidConsultations?.length > 0) {
      setError(
        `Cannot close package. ${unpaidConsultations.length} unpaid consultation(s) pending.`,
      );
      return;
    }
    // 🔴 SAFETY CHECK 1: Due amount must be 0
    if (totalDueAmount > 0) {
      setError(
        `Cannot close package. Pending due balance: ₹ ${totalDueAmount}`,
      );
      return;
    }

    // 🔴 SAFETY CHECK 2: Package sessions must be fully used (optional rule)
    // 🔴 SAFETY CHECK 2: Package sessions remaining
    if (
      activePackage &&
      activePackage.remaining_days > 0 &&
      !confirmForceClose
    ) {
      setError(
        `Patient still has ${activePackage.remaining_days} sessions left. Please confirm to force close package.`,
      );
      return;
    }

    setLoading(true);

    try {
      // 🟢 MAIN LOGIC: Mark package as completed (NOT delete history)
      await updateDoc(doc(db, "packages", activePackage.id), {
        status: "COMPLETED",
        closed_at: serverTimestamp(),
        updated_at: serverTimestamp(),
      });

      // Optional: also mark active package as completed
      if (activePackage?.id) {
        await updateDoc(doc(db, "packages", activePackage.id), {
          status: "CLOSED", // 🔥 change here
          package_status: "COMPLETED",
          closed_at: serverTimestamp(),
          updated_at: serverTimestamp(),
        });
        await updateDoc(doc(db, "patients", patient.id), {
  active_package: false,
});
      }
      

      if (onSuccess) {
        await onSuccess();
      }

      onClose();
    } catch (err) {
      console.error("Error closing package:", err);
      setError("Failed to close package. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-[95%] max-w-md shadow-lg">
        <h3 className="text-lg font-semibold text-[#204080] mb-4">
          Close Package
        </h3>

        {/* Patient Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm space-y-1">
          <p>
            <span className="font-semibold">Patient:</span> {patient.name}
          </p>
          <p>
            <span className="font-semibold">VPJC ID:</span> {patient.vpjc_id}
          </p>

          {activePackage && (
            <>
              <p className="text-blue-600">
                <span className="font-semibold">Remaining Sessions:</span>{" "}
                {activePackage.remaining_days}
              </p>
            </>
          )}

          <p
            className={`font-semibold ${
              totalDueAmount > 0 ? "text-red-600" : "text-green-600"
            }`}
          >
            Due Balance: ₹ {totalDueAmount}
          </p>
        </div>

        {/* Warning */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-xs text-yellow-800 mb-4">
          Closing package will:
          <ul className="list-disc ml-4 mt-1">
            <li>Stop active package</li>
            <li>Keep full patient history</li>
            <li>Allow future new package cycles</li>
          </ul>
        </div>

        {/* Error */}
        {error && (
          <p className="text-sm text-red-600 mb-3 font-medium">{error}</p>
        )}
        {/* Force Close Confirmation (only if sessions left) */}
        {activePackage && activePackage.remaining_days > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-4">
            <p className="font-semibold mb-2">
              Patient still has {activePackage.remaining_days} sessions
              remaining.
            </p>

            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={confirmForceClose}
                onChange={(e) => {
                  setConfirmForceClose(e.target.checked);
                  setError("");
                }}
              />
              I understand and want to close package anyway
            </label>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className="px-4 py-2 border rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={handleClosePackage}
            disabled={loading}
            className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60"
          >
            {loading ? "Closing..." : "Close Package"}
          </button>
        </div>
      </div>
    </div>
  );
}
